import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from "@reduxjs/toolkit"
import { SortOrders } from '../../globals';
import { AssetCollections, IAssetLibrary } from "../../interfaces/AssetLibrary";
import { removeUndefinedEntriesFromObjectRecursive } from '../../classes/utilities/Misc';
import { IAssetInfo, IProjectAssetInfo } from '../../interfaces/Assets';

export interface IAssetsSliceState {
	updater: number;
	library: IAssetLibrary | null;
	project: Record<string, IProjectAssetInfo>;
	foreign: Record<string, IAssetInfo>;
	selectedAssets: string[];
	sortOrder: SortOrders;
	previewScale: number;
	imagePickerScale: number;
	renderSettings: Record<string, any>;
}

const initialState: IAssetsSliceState = {
	updater: 0,
	library: null,
	project: {},
	foreign: {},
	selectedAssets: [],
	sortOrder: SortOrders.Ascending,
	previewScale: 1,
	imagePickerScale: 1,
	renderSettings: {},
};

function sanitizeLibrary(library: IAssetLibrary): IAssetLibrary
{
	if(!library?.user || !library?.organization)
	{
		console.error("missing library data!", library);
		return library;
	}	

	const output = {
		user: removeUndefinedEntriesFromObjectRecursive(library.user),
		organization: removeUndefinedEntriesFromObjectRecursive(library.organization),
	};

	function removeIllegalFolders(subCollection: Record<string, IAssetInfo>)
	{
		for(const assetInfo of Object.values(subCollection))
		{
			if(assetInfo.folder && !subCollection[assetInfo.folder])
			{
				delete assetInfo.folder;
			}
		}
	}

	removeIllegalFolders(output.user);
	removeIllegalFolders(output.organization);

	return output;
}

const AssetsSlice = createSlice({
	name: "assets",
	initialState: initialState,
	reducers: {
		update(state, action: PayloadAction<void>)
		{
			state.updater = state.updater + 1;
		},
		setLibrary(state, action: PayloadAction<IAssetLibrary>)
		{
			state.library = sanitizeLibrary(action.payload);
		},
		setProjectAssets(state, action: PayloadAction<Record<string, IProjectAssetInfo>>)
		{
			state.project = action.payload;
		},
		setSelectedAssets(state, action: PayloadAction<string[]>)
		{
			state.selectedAssets = action.payload;
		},
		setSortOrder(state, action: PayloadAction<SortOrders>)
		{
			state.sortOrder = action.payload;
		},
		setPreviewScale(state, action: PayloadAction<number>)
		{
			state.previewScale = action.payload;
		},
		setImagePickerScale(state, action: PayloadAction<number>)
		{
			state.imagePickerScale = action.payload;
		},
		setRenderSettings(state, action: PayloadAction<Record<string, any>>)
		{
			state.renderSettings = action.payload;
		},
		addAssetToLibrary(state, action: PayloadAction<{ collection: AssetCollections, assetId: string, assetInfo: IAssetInfo }>)
		{
			if(state.library)
			{
				switch(action.payload.collection)
				{
					case AssetCollections.User:
						state.library.user[action.payload.assetId] = action.payload.assetInfo;
						break;
					case AssetCollections.Organization:
						state.library.organization[action.payload.assetId] = action.payload.assetInfo;
						break;
					case AssetCollections.Foreign:
						state.foreign[action.payload.assetId] = action.payload.assetInfo;						
						break;
					default:
						console.error("unknown collection", action.payload.collection);
						break;
				}
			}
		},
	},
});

export default AssetsSlice;
